<template>
    <div class="title-banner-microlearning">
        <div class="title-banner-microlearning__top">
            <div class="title-banner-microlearning__content">
                <span v-if="splitTitle.number" class="number" v-html="splitTitle.number"></span>
                <span v-else-if="currentMicroLearningSection?.type === 'file'" class="icon-file-text"></span>
                <div>
                    <h1 class="title-banner-microlearning__title" v-html="splitTitle.text"></h1>
                </div>
            </div>
            <div  v-if="isMobileView" class="title-banner-microlearning__breadcrumb-and-subtitle">
                <Breadcrumb :theme=theme />
                <span v-if="theme === 'microlearning'" class="title-banner-microlearning__microlearning-path" v-html="getMicroLearningSectionSubTitle"></span>
            </div>
            <div class="title-banner-microlearning__actions">
                <div class="title-banner-microlearning__rating -rating">
                    <TotemButton
                        v-if="isLoggedIn"
                        size="medium"
                        color="secondary"
                        @click="showRatingModal"
                        :class="['title-banner-microlearning__rating-button', {'pulse-animation': !ratingSaved }]"
                        :label="ratingButtonLabel"
                        icon="star"
                    />
                </div>
                <div class="title-banner-microlearning__watchlist-export">
                    <TotemAddToWatchlistButton
                        v-if="showAddToFavourite"
                        :elementId="course.id"
                        alignment="course-page"
                    />
                    <ExportLSPContent v-if="course.canBeExportedToLSP && $totemLang !== 'dk'" :course="course" />
                </div>
            </div>
        </div>
        <div  v-if="isDesktopView" class="title-banner-microlearning__breadcrumb-and-subtitle">
            <Breadcrumb :theme=theme />
            <span v-if="theme === 'microlearning'" class="title-banner-microlearning__microlearning-path" v-html="getMicroLearningSectionSubTitle"></span>
        </div>
    </div>
</template>

<script>
    import ExportLSPContent from 'components/coursepage/banner/ExportLSPContent'
    import { mapGetters } from 'vuex'
    import courseRatingHelper from 'components/shared/global-courses-items/helper/course-rating-helper'
    import Breadcrumb from 'components/breadcrumb/breadcrumb'

    export default {
        components: {
            ExportLSPContent,
            Breadcrumb
        },
        mixins: [ courseRatingHelper ],
        props: {
            course: Object,
            theme: {
                type: String,
                validator: (prop) => [ 'course', 'microlearning' ].includes(prop),
                default: 'course'
            },
        },
        computed: {
            ...mapGetters({
                currentMicroLearningSection: 'MicroLearning/getMicroLearningSection',
                isLoggedIn: 'isLoggedIn',
                ratingSaved: 'Ratings/getRatingSavedState',
                getCurrentMicroLearningSectionTitle: 'MicroLearning/getMicroLearningSectionTitle',
                microLearningSections: 'MicroLearning/getMicroLearningSections'
            }),
            getMicroLearningSectionSubTitle () {
                if (this.currentMicroLearningSection !== null) {
                    return this.course.title
                }
                return this.course.title
            },
            getMicroLearningSectionTitle () {
                if (this.currentMicroLearningSection === null) {
                    return this.course.title
                } else {
                    return this.currentMicroLearningSection.title
                }
            },
            splitTitle() {
                if (this.microLearningSections === null) {
                    return {
                        number: '',
                        text: this.course.title
                    }
                }
                const index = this.microLearningSections.findIndex(
                    section => section.title === this.getMicroLearningSectionTitle
                )
                return {
                    number: index >= 0 ? String(index + 1).padStart(2, '0') : '',
                    text: this.getMicroLearningSectionTitle
                }
            },
            showAddToFavourite () {
                return this.isLoggedIn
            },
            ratingButtonLabel () {
                return this.ratingSaved ? this.$t('Rating.edit_your_rating_button') : this.$t('Rating.rate_this_course_button')
            }
        },
        methods: {
            // Needs a mixin for rating stuff
            showRatingModal () {
                this.$modal.show('course-rating')
            }
        }
    }
</script>

<style lang="scss" scoped>
    .title-banner-microlearning {
        flex-direction: column;
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin: 0 0 20px;

        [class*="mobile"] & {
            flex-direction: column;
            margin: 0px 8px 20px;
        }

        &__content {
            display: flex;
            flex-direction: row;
            flex-grow: 1;
            align-items: center;
        }

        &__top {
            display: flex;
            flex-direction: row;
            width: 100%;

            [class*="mobile"] & {
                flex-wrap: wrap;
                gap: 12px;
                flex-direction: column;
            }
        }

        .number {
            color: $white;
            background-color: $lavender;
            padding: 7px 9px;
            border-radius: 4px;
            margin: 3px 20px 0 0;
            height: 32px;
            display: flex;
            align-items: center;
            font-size: 2.5rem;
            font-family: PPAgrandir-Medium, sans-serif;
        }

        .icon-file-text {
            color: $white;
            background-color: $lavender;
            padding: 7px 9px;
            border-radius: 4px;
            margin: 3px 20px 0 0;
            height: 32px;
            display: flex;
            align-items: center;
            font-size: 2.5rem;
            font-family: PPAgrandir-Medium, sans-serif;
        }

        &__title {
            font-family: PPAgrandir-Medium, sans-serif;
            font-size: 3.2rem;

            [class*="desktop"] & {
                padding: 0 0 10px;
            }
            [class*="mobile"] & {
                font-size: 2.4rem;
                word-break: break-word;
            }
        }

        &__actions {
            display: flex;
            flex-direction: row;
            gap: 20px;
            margin: 0px 8px;
            justify-content: flex-end;
            align-items: baseline;

            [class*="mobile"] & {
                justify-content: flex-start;
                margin: 0;
            }
        }

        &__microlearning-path {
            font-size: 1.6rem;
            font-family: PlusJakartaSans-Medium, sans-serif;
        }

        &__rating-button {
            white-space: nowrap;
            position: relative;
            z-index: 1;

            &.pulse-animation {
                &:before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    border-radius: 30px;
                    box-shadow: 0 0 0 0 rgba(0, 0, 0, 1);
                    transform: scale(1);
                    animation: pulse 2s;
                    animation-iteration-count: 3;
                    z-index: 0;
                }
            }
        }

        &__watchlist-export {
            display: flex;
            flex-direction: row;
            gap: 20px;
        }

        &__breadcrumb-and-subtitle {
            display: flex;
            flex-direction: row;
            align-items: center;
            flex-wrap: wrap;
        }

        &__breadcrumb-separator {
            color: $lavender
        }
    }
</style>
